import { useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie,
  PieChart, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart,
  Tooltip, XAxis, YAxis, ZAxis,
} from "recharts";
import { api, ApiError } from "../api";
import { kvOf, type Measurement, type Period, type Turbine, type TurbineEvent } from "../types";
import { tg } from "../telegram";
import { useT } from "../i18n";
import {
  analyseCompare, analyseCosKvar, analyseFrequency, analyseKwHistogram,
  analyseKwKv, analysePressure, analyseScatter, analyseStopReasons,
  analyseTemperatures, analyseTimeline, type AnalysisItem,
} from "../utils/chartAnalysis";

type ChartKind =
  | "kw_kv"
  | "compare_turbines"
  | "timeline"
  | "temperatures"
  | "frequency"
  | "stop_reasons"
  | "kw_histogram"
  | "cos_kvar"
  | "pressure"
  | "kw_kv_scatter";

// Chart kinds that work on data for a single selected turbine.
const SINGLE_TURBINE_KINDS: ChartKind[] = [
  "kw_kv", "temperatures", "frequency", "cos_kvar", "pressure",
  "kw_kv_scatter", "kw_histogram",
];
// Chart kinds that pull the entire turbine_events log (no measurements).
const EVENT_KINDS: ChartKind[] = ["timeline", "stop_reasons"];

// Palette for multi-line temperature / pressure charts.
const TEMP_COLORS = [
  "#2481cc", "#34c759", "#ff9500", "#a259ff", "#ff3b30",
  "#00c7be", "#ffcc00", "#ff2d55",
];

export default function Charts() {
  const { t, lang } = useT();
  const [kind, setKind] = useState<ChartKind>("kw_kv");
  const [period, setPeriod] = useState<Period>("7d");
  const [turbines, setTurbines] = useState<Turbine[]>([]);
  const [turbineId, setTurbineId] = useState<number | null>(null);
  const [items, setItems] = useState<Measurement[] | null>(null);
  const [events, setEvents] = useState<TurbineEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const PERIODS: { key: Period; label: string }[] = [
    { key: "today", label: t("period.today") },
    { key: "yest",  label: t("period.yest") },
    { key: "7d",    label: t("period.7d") },
    { key: "30d",   label: t("period.30d") },
    { key: "all",   label: t("period.all") },
  ];

  const needsTurbinePicker = SINGLE_TURBINE_KINDS.includes(kind);
  const needsMeasurements = !EVENT_KINDS.includes(kind);
  const needsEvents = EVENT_KINDS.includes(kind);

  useEffect(() => {
    api.get<Turbine[]>("/api/turbines")
      .then(list => {
        setTurbines(list);
        if (turbineId === null && list.length > 0) setTurbineId(list[0].id);
      })
      .catch((e: ApiError) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (needsTurbinePicker && turbineId === null) return;
    const { from, to } = periodWindow(period);
    setError(null);
    setLoading(true);

    const tasks: Promise<unknown>[] = [];
    if (needsMeasurements) {
      const params = new URLSearchParams({ from, to, limit: "2000" });
      if (needsTurbinePicker && turbineId !== null) {
        params.set("turbine_id", String(turbineId));
      }
      setItems(null);
      tasks.push(
        api.get<Measurement[]>(`/api/measurements?${params.toString()}`).then(rows => {
          rows.sort((a, b) => a.slot_time.localeCompare(b.slot_time));
          setItems(rows);
        })
      );
    }
    if (needsEvents) {
      setEvents(null);
      tasks.push(
        api.get<TurbineEvent[]>(`/api/turbines/events?limit=200`).then(rows => {
          rows.sort((a, b) => a.event_time.localeCompare(b.event_time));
          setEvents(rows);
        })
      );
    }
    Promise.all(tasks)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, turbineId, period]);

  const onPeriod = (p: Period) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    setPeriod(p);
  };
  const onTurbine = (id: number) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    setTurbineId(id);
  };
  const onKind = (k: ChartKind) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    setKind(k);
  };

  const selectedTurbine = turbines.find(tb => tb.id === turbineId);

  // List of chart kinds with translated labels — used both for the <select>
  // and to surface the description hint under the chart title.
  const KIND_OPTIONS: { key: ChartKind; label: string; hint: string }[] = [
    { key: "kw_kv",            label: t("charts.kw_kv_label"),            hint: t("charts.kw_kv_hint") },
    { key: "compare_turbines", label: t("charts.compare_label"),          hint: t("charts.compare_hint") },
    { key: "timeline",         label: t("charts.timeline_label"),         hint: t("charts.timeline_hint") },
    { key: "temperatures",     label: t("charts.temperatures_label"),     hint: t("charts.temperatures_hint") },
    { key: "frequency",        label: t("charts.frequency_label"),        hint: t("charts.frequency_hint") },
    { key: "stop_reasons",     label: t("charts.stop_reasons_label"),     hint: t("charts.stop_reasons_hint") },
    { key: "kw_histogram",     label: t("charts.kw_histogram_label"),     hint: t("charts.kw_histogram_hint") },
    { key: "cos_kvar",         label: t("charts.cos_kvar_label"),         hint: t("charts.cos_kvar_hint") },
    { key: "pressure",         label: t("charts.pressure_label"),         hint: t("charts.pressure_hint") },
    { key: "kw_kv_scatter",    label: t("charts.scatter_label"),          hint: t("charts.scatter_hint") },
  ];
  const currentKindMeta = KIND_OPTIONS.find(o => o.key === kind)!;

  return (
    <div>
      <h1>{t("charts.title")}</h1>

      <div className="card">
        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 6 }}>
          {t("charts.select_chart")}
        </div>
        <select
          value={kind}
          onChange={e => onKind(e.target.value as ChartKind)}
          style={{ marginBottom: 12 }}
        >
          {KIND_OPTIONS.map(o => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>

        {needsTurbinePicker && turbines.length > 1 && (
          <>
            <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 6 }}>
              {t("charts.turbine")}
            </div>
            <div className="turbine-switcher" style={{ marginBottom: 12 }}>
              {turbines.map(tb => (
                <button
                  key={tb.id}
                  className={"turbine-chip" + (tb.id === turbineId ? " active" : "")}
                  onClick={() => onTurbine(tb.id)}
                >
                  <span className="turbine-chip-name">{tb.name}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 6 }}>
          {t("charts.period")}
        </div>
        <div className="period-tabs">
          {PERIODS.map(p => (
            <button
              key={p.key}
              className={"period-tab" + (p.key === period ? " active" : "")}
              onClick={() => onPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="alert error">⚠ {error}</div>}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>{currentKindMeta.label}</h2>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 12 }}>
          {currentKindMeta.hint}
        </div>

        {loading && <div className="empty-state" style={{ padding: 40 }}><span className="spinner" /></div>}

        {!loading && needsMeasurements && items && items.length === 0 && (
          <div className="empty-state">{t("common.no_data")}</div>
        )}
        {!loading && needsEvents && events && events.length === 0 && (
          <div className="empty-state">{t("common.no_data")}</div>
        )}

        {!loading && items && needsMeasurements && (
          <>
            {kind === "kw_kv" && (<>
              <KwKvChart items={items} lang={lang} />
              <ChartAnalysis items={analyseKwKv(items, t)} t={t} />
            </>)}
            {kind === "compare_turbines" && (<>
              <CompareTurbinesChart items={items} turbines={turbines} lang={lang} />
              <ChartAnalysis items={analyseCompare(items, turbines, t)} t={t} />
            </>)}
            {kind === "temperatures" && (<>
              <TemperaturesChart items={items} lang={lang} />
              <ChartAnalysis items={analyseTemperatures(items, t)} t={t} />
            </>)}
            {kind === "frequency" && (<>
              <FrequencyChart items={items} lang={lang} t={t} />
              <ChartAnalysis items={analyseFrequency(items, t)} t={t} />
            </>)}
            {kind === "cos_kvar" && (<>
              <CosKvarChart items={items} lang={lang} />
              <ChartAnalysis items={analyseCosKvar(items, t)} t={t} />
            </>)}
            {kind === "pressure" && (<>
              <PressureChart items={items} lang={lang} />
              <ChartAnalysis items={analysePressure(items, t)} t={t} />
            </>)}
            {kind === "kw_kv_scatter" && (<>
              <KwKvScatter items={items} />
              <ChartAnalysis items={analyseScatter(items, t)} t={t} />
            </>)}
            {kind === "kw_histogram" && (<>
              <KwHistogram items={items} />
              <ChartAnalysis items={analyseKwHistogram(items, t)} t={t} />
            </>)}
          </>
        )}
        {!loading && events && needsEvents && (
          <>
            {kind === "timeline" && (<>
              <TimelineChart events={events} turbines={turbines} period={period} t={t} lang={lang} />
              <ChartAnalysis items={analyseTimeline(events, turbines, period, t)} t={t} />
            </>)}
            {kind === "stop_reasons" && (<>
              <StopReasonsChart events={events} t={t} />
              <ChartAnalysis items={analyseStopReasons(events, t)} t={t} />
            </>)}
          </>
        )}
      </div>

      {/* KW/KV: also show offline records as a second card below. */}
      {kind === "kw_kv" && !loading && items && items.some(m => m.is_offline) && (
        <KwKvOfflineCard items={items} lang={lang} t={t} />
      )}

      {/* Compare turbines: also show per-turbine summary */}
      {kind === "compare_turbines" && !loading && items && selectedTurbine == null && null}
    </div>
  );
}


/* ============================================================================
 *  Individual chart components — each one takes raw data and renders.
 * ========================================================================= */

function KwKvChart({ items, lang }: { items: Measurement[]; lang: string }) {
  const data = useMemo(() =>
    items
      .filter(m => !m.is_offline && (m.kw !== null || kvOf(m) !== null))
      .map(m => ({
        label: fmtAxisLabel(m.slot_time, lang),
        tooltipTime: fmtTooltipTime(m.slot_time, lang),
        kw: m.kw, kv: kvOf(m),
      })),
  [items, lang]);
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
        <YAxis yAxisId="kw" orientation="left" tick={{ fontSize: 10, fill: "#2481cc" }}
          label={{ value: "KW", angle: -90, position: "insideLeft", fontSize: 11, fill: "#2481cc" }} />
        <YAxis yAxisId="kv" orientation="right" tick={{ fontSize: 10, fill: "#34c759" }}
          label={{ value: "KV", angle: 90, position: "insideRight", fontSize: 11, fill: "#34c759" }} />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line yAxisId="kw" type="monotone" dataKey="kw" name="KW" stroke="#2481cc"
              strokeWidth={2} dot={{ r: 2 }} connectNulls isAnimationActive={false} />
        <Line yAxisId="kv" type="monotone" dataKey="kv" name="KV" stroke="#34c759"
              strokeWidth={2} dot={{ r: 2 }} connectNulls isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function KwKvOfflineCard({ items, lang, t }: {
  items: Measurement[]; lang: string;
  t: (k: string, p?: Record<string, string | number>) => string;
}) {
  const data = items
    .filter(m => m.is_offline && (m.kw !== null || kvOf(m) !== null))
    .map(m => ({
      label: fmtAxisLabel(m.slot_time, lang),
      tooltipTime: fmtTooltipTime(m.slot_time, lang),
      kw: m.kw, kv: kvOf(m),
    }));
  if (data.length === 0) return null;
  return (
    <div className="card">
      <h2>{t("charts.kw_kv_offline_title")}</h2>
      <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 12 }}>
        {t("charts.kw_kv_offline_hint")}
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
          <YAxis yAxisId="kw" orientation="left" tick={{ fontSize: 10, fill: "#ff9500" }}
            label={{ value: "KW", angle: -90, position: "insideLeft", fontSize: 11, fill: "#ff9500" }} />
          <YAxis yAxisId="kv" orientation="right" tick={{ fontSize: 10, fill: "#a259ff" }}
            label={{ value: "KV", angle: 90, position: "insideRight", fontSize: 11, fill: "#a259ff" }} />
          <Tooltip content={<ChartTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line yAxisId="kw" type="monotone" dataKey="kw" name="KW" stroke="#ff9500"
                strokeWidth={2} dot={{ r: 4, fill: "#ff9500" }} connectNulls isAnimationActive={false} />
          <Line yAxisId="kv" type="monotone" dataKey="kv" name="KV" stroke="#a259ff"
                strokeWidth={2} dot={{ r: 4, fill: "#a259ff" }} connectNulls isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CompareTurbinesChart({ items, turbines, lang }: {
  items: Measurement[]; turbines: Turbine[]; lang: string;
}) {
  // Build one row per timestamp with one column per turbine_id.
  const data = useMemo(() => {
    const byTs = new Map<string, Record<string, number | string | null>>();
    for (const m of items) {
      if (m.is_offline || m.kw === null || m.turbine_id === null) continue;
      const key = m.slot_time;
      if (!byTs.has(key)) {
        byTs.set(key, {
          label: fmtAxisLabel(m.slot_time, lang),
          tooltipTime: fmtTooltipTime(m.slot_time, lang),
        });
      }
      byTs.get(key)![`t${m.turbine_id}`] = m.kw;
    }
    return Array.from(byTs.values());
  }, [items, lang]);

  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
        <YAxis tick={{ fontSize: 10 }} label={{ value: "KW", angle: -90, position: "insideLeft", fontSize: 11 }} />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {turbines.map((tb, idx) => (
          <Line key={tb.id} type="monotone" dataKey={`t${tb.id}`} name={tb.name}
                stroke={TEMP_COLORS[idx % TEMP_COLORS.length]} strokeWidth={2}
                dot={{ r: 2 }} connectNulls isAnimationActive={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function TemperaturesChart({ items, lang }: { items: Measurement[]; lang: string }) {
  const temps = [
    { key: "bt_371", label: "BT 371" },
    { key: "bt_372", label: "BT 372" },
    { key: "bt_331", label: "BT 331" },
    { key: "bt_332", label: "BT 332" },
  ];
  const data = useMemo(() =>
    items.filter(m => !m.is_offline).map(m => {
      const row: Record<string, string | number | null> = {
        label: fmtAxisLabel(m.slot_time, lang),
        tooltipTime: fmtTooltipTime(m.slot_time, lang),
      };
      for (const tdef of temps) {
        row[tdef.key] = (m as unknown as Record<string, number | null>)[tdef.key] ?? null;
      }
      return row;
    }),
  [items, lang]);
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
        <YAxis tick={{ fontSize: 10 }} label={{ value: "°C", angle: -90, position: "insideLeft", fontSize: 11 }} />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {temps.map((td, idx) => (
          <Line key={td.key} type="monotone" dataKey={td.key} name={td.label}
                stroke={TEMP_COLORS[idx % TEMP_COLORS.length]} strokeWidth={1.5}
                dot={false} connectNulls isAnimationActive={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function FrequencyChart({ items, lang, t }: {
  items: Measurement[]; lang: string;
  t: (k: string, p?: Record<string, string | number>) => string;
}) {
  const data = useMemo(() =>
    items.filter(m => !m.is_offline && m.frequency !== null).map(m => ({
      label: fmtAxisLabel(m.slot_time, lang),
      tooltipTime: fmtTooltipTime(m.slot_time, lang),
      frequency: m.frequency,
    })),
  [items, lang]);
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
        <YAxis tick={{ fontSize: 10 }} domain={[48, 52]}
          label={{ value: "Hz", angle: -90, position: "insideLeft", fontSize: 11 }} />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine y={49.5} stroke="#ff9500" strokeDasharray="4 4"
          label={{ value: t("charts.freq_low"), position: "insideTopLeft", fontSize: 10, fill: "#ff9500" }} />
        <ReferenceLine y={50.5} stroke="#ff9500" strokeDasharray="4 4"
          label={{ value: t("charts.freq_high"), position: "insideBottomLeft", fontSize: 10, fill: "#ff9500" }} />
        <ReferenceLine y={50} stroke="#34c759" strokeDasharray="2 2"
          label={{ value: "50.0", position: "right", fontSize: 10, fill: "#34c759" }} />
        <Line type="monotone" dataKey="frequency" name="Frequency Hz"
              stroke="#2481cc" strokeWidth={2} dot={{ r: 2 }} connectNulls isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function CosKvarChart({ items, lang }: { items: Measurement[]; lang: string }) {
  const data = useMemo(() =>
    items.filter(m => !m.is_offline && (m.cos_phi !== null || m.kvar !== null)).map(m => ({
      label: fmtAxisLabel(m.slot_time, lang),
      tooltipTime: fmtTooltipTime(m.slot_time, lang),
      cos_phi: m.cos_phi, kvar: m.kvar,
    })),
  [items, lang]);
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
        <YAxis yAxisId="cos" orientation="left" tick={{ fontSize: 10, fill: "#2481cc" }} domain={[0, 1]}
          label={{ value: "cos φ", angle: -90, position: "insideLeft", fontSize: 11, fill: "#2481cc" }} />
        <YAxis yAxisId="kvar" orientation="right" tick={{ fontSize: 10, fill: "#a259ff" }}
          label={{ value: "Kvar", angle: 90, position: "insideRight", fontSize: 11, fill: "#a259ff" }} />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line yAxisId="cos"  type="monotone" dataKey="cos_phi" name="cos φ" stroke="#2481cc"
              strokeWidth={2} dot={{ r: 2 }} connectNulls isAnimationActive={false} />
        <Line yAxisId="kvar" type="monotone" dataKey="kvar"    name="Kvar"  stroke="#a259ff"
              strokeWidth={2} dot={{ r: 2 }} connectNulls isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function PressureChart({ items, lang }: { items: Measurement[]; lang: string }) {
  const fields = [
    { key: "bp_371", label: "BP 371" },
    { key: "pn_351", label: "PN 351" },
    { key: "pn_352", label: "PN 352" },
  ];
  const data = useMemo(() =>
    items.filter(m => !m.is_offline).map(m => {
      const row: Record<string, string | number | null> = {
        label: fmtAxisLabel(m.slot_time, lang),
        tooltipTime: fmtTooltipTime(m.slot_time, lang),
      };
      for (const f of fields) row[f.key] = (m as unknown as Record<string, number | null>)[f.key] ?? null;
      return row;
    }),
  [items, lang]);
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
        <YAxis tick={{ fontSize: 10 }} label={{ value: "bar", angle: -90, position: "insideLeft", fontSize: 11 }} />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {fields.map((f, idx) => (
          <Line key={f.key} type="monotone" dataKey={f.key} name={f.label}
                stroke={TEMP_COLORS[idx % TEMP_COLORS.length]} strokeWidth={2}
                dot={{ r: 2 }} connectNulls isAnimationActive={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function KwKvScatter({ items }: { items: Measurement[] }) {
  const data = useMemo(() =>
    items.filter(m => !m.is_offline && m.kw !== null && kvOf(m) !== null)
      .map(m => ({ kw: m.kw as number, kv: kvOf(m) as number })),
  [items]);
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <ScatterChart margin={{ top: 12, right: 10, left: 0, bottom: 12 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
        <XAxis type="number" dataKey="kw" name="KW" tick={{ fontSize: 10 }}
          label={{ value: "KW", position: "insideBottom", offset: -2, fontSize: 11 }} />
        <YAxis type="number" dataKey="kv" name="KV" tick={{ fontSize: 10 }}
          label={{ value: "KV", angle: -90, position: "insideLeft", fontSize: 11 }} />
        <ZAxis range={[40, 40]} />
        <Tooltip cursor={{ strokeDasharray: "3 3" }}
          formatter={(v: number) => Number.isInteger(v) ? v : v.toFixed(1)} />
        <Scatter name="KW vs KV" data={data} fill="#2481cc" isAnimationActive={false} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function KwHistogram({ items }: { items: Measurement[] }) {
  // Bin KW values into 200-unit buckets.
  const bucketSize = 200;
  const data = useMemo(() => {
    const vals = items.filter(m => !m.is_offline && m.kw !== null).map(m => m.kw as number);
    if (vals.length === 0) return [];
    const maxBucket = Math.ceil(Math.max(...vals) / bucketSize);
    const counts = new Array(maxBucket + 1).fill(0);
    for (const v of vals) {
      const idx = Math.min(maxBucket, Math.floor(v / bucketSize));
      counts[idx] += 1;
    }
    return counts.map((c, idx) => ({
      bucket: `${idx * bucketSize}-${(idx + 1) * bucketSize}`,
      count: c,
    }));
  }, [items]);
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 18 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
        <XAxis dataKey="bucket" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} height={50} />
        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
        <Tooltip formatter={(v: number) => v} />
        <Bar dataKey="count" fill="#2481cc" isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function TimelineChart({ events, turbines, period, t, lang }: {
  events: TurbineEvent[]; turbines: Turbine[]; period: Period;
  t: (k: string, p?: Record<string, string | number>) => string;
  lang: string;
}) {
  // For each turbine, walk events chronologically and build segments
  // [start_time, end_time, state] within the period.
  const { from, to } = periodWindow(period);
  const periodStart = new Date(from).getTime();
  const periodEnd = new Date(to).getTime() + 86400 * 1000;
  const now = Date.now();
  const endEff = Math.min(periodEnd, now);
  const totalMs = endEff - periodStart;

  if (totalMs <= 0 || turbines.length === 0) {
    return <div className="empty-state">{t("common.no_data")}</div>;
  }

  // Build segments per turbine. For each turbine we only render the part of
  // the period for which we actually have data — i.e. starting at the
  // turbine's first-ever event. Anything earlier stays as the empty grey
  // background of the row instead of being painted red (stopped) by default.
  type Segment = { from: number; to: number; running: boolean };
  const byTurbine = new Map<number, Segment[]>();
  for (const tb of turbines) {
    const evs = events.filter(e => e.turbine_id === tb.id);
    if (evs.length === 0) {
      byTurbine.set(tb.id, []);
      continue;
    }
    const firstEventTime = new Date(evs[0].event_time).getTime();
    const effectiveStart = Math.max(periodStart, firstEventTime);
    if (endEff <= effectiveStart) {
      byTurbine.set(tb.id, []);
      continue;
    }

    // State at effectiveStart — last event strictly before it, if any.
    const before = evs.filter(e => new Date(e.event_time).getTime() < effectiveStart);
    let running = before.length > 0 && before[before.length - 1].event_type === "start";

    const inside = evs.filter(e => {
      const t0 = new Date(e.event_time).getTime();
      return t0 >= effectiveStart && t0 < endEff;
    });

    const segments: Segment[] = [];
    let cursor = effectiveStart;
    for (const e of inside) {
      const at = new Date(e.event_time).getTime();
      if (at > cursor) segments.push({ from: cursor, to: at, running });
      running = e.event_type === "start";
      cursor = at;
    }
    if (cursor < endEff) segments.push({ from: cursor, to: endEff, running });
    byTurbine.set(tb.id, segments);
  }

  const ROW_H = 30;
  const ROW_GAP = 12;
  const LABEL_W = 80;
  const totalH = (ROW_H + ROW_GAP) * turbines.length;

  // Currently-opened tooltip (kept in component state so we can render it
  // as a real DOM node, not a native browser `title=""`. The latter does
  // not work on touch devices and looks plain on dark themes).
  const [active, setActive] = useState<null | {
    tbId: number; segIdx: number;
    name: string; stateLbl: string; dur: string;
    fromTxt: string; toTxt: string;
    leftPct: number; widthPct: number; rowTop: number;
    running: boolean;
  }>(null);

  // Click anywhere outside a segment / tooltip → close it.
  useEffect(() => {
    if (!active) return;
    const handler = (e: Event) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (target.closest(".timeline-segment") || target.closest(".timeline-tooltip")) return;
      setActive(null);
    };
    document.addEventListener("pointerdown", handler, true);
    return () => document.removeEventListener("pointerdown", handler, true);
  }, [active]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between",
                    fontSize: 11, color: "var(--hint)", marginBottom: 8, marginLeft: LABEL_W }}>
        <span>{fmtTooltipTime(new Date(periodStart).toISOString(), lang)}</span>
        <span>{fmtTooltipTime(new Date(endEff).toISOString(), lang)}</span>
      </div>
      <div style={{ position: "relative", height: totalH }}>
        {turbines.map((tb, idx) => {
          const top = idx * (ROW_H + ROW_GAP);
          const segments = byTurbine.get(tb.id) ?? [];
          return (
            <div key={tb.id} style={{ position: "absolute", top, left: 0, right: 0, height: ROW_H,
                                      display: "flex", alignItems: "center" }}>
              <div style={{ width: LABEL_W, fontSize: 12, fontWeight: 600, paddingRight: 8 }}>
                {tb.name}
              </div>
              <div style={{ position: "relative", flex: 1, height: ROW_H,
                            background: "rgba(127,127,127,0.08)", borderRadius: 4 }}>
                {segments.map((seg, sidx) => {
                  const leftPct = ((seg.from - periodStart) / totalMs) * 100;
                  const widthPct = ((seg.to - seg.from) / totalMs) * 100;
                  const isActive = !!active && active.tbId === tb.id && active.segIdx === sidx;
                  const onTap = () => {
                    if (isActive) { setActive(null); return; }
                    const durMin = Math.round((seg.to - seg.from) / 60000);
                    setActive({
                      tbId: tb.id, segIdx: sidx,
                      name: tb.name,
                      stateLbl: seg.running ? t("charts.timeline_running") : t("charts.timeline_stopped"),
                      dur: fmtDurationShort(durMin, t),
                      fromTxt: fmtTooltipTime(new Date(seg.from).toISOString(), lang),
                      toTxt: fmtTooltipTime(new Date(seg.to).toISOString(), lang),
                      leftPct, widthPct, rowTop: top, running: seg.running,
                    });
                    tg()?.HapticFeedback?.selectionChanged?.();
                  };
                  return (
                    <div
                      key={sidx}
                      className={`timeline-segment ${seg.running ? "running" : "stopped"}${isActive ? " active" : ""}`}
                      onClick={onTap}
                      style={{ position: "absolute", top: 0, bottom: 0,
                               left: `${leftPct}%`, width: `${widthPct}%` }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Custom tooltip — looks like our recharts tooltips, taps work on
            touch devices, closes on outside click. Positioned above the
            tapped segment, anchored by its centre. */}
        {active && (
          <div
            className={`timeline-tooltip ${active.running ? "running" : "stopped"}`}
            style={{
              position: "absolute",
              left: `calc(${LABEL_W}px + (100% - ${LABEL_W}px) * ${(active.leftPct + active.widthPct / 2) / 100})`,
              top: active.rowTop - 6,
              transform: "translate(-50%, -100%)",
              maxWidth: "min(280px, calc(100% - 16px))",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="timeline-tooltip-head">
              <span className="timeline-tooltip-name">{active.name}</span>
              <span className={`timeline-tooltip-pill ${active.running ? "running" : "stopped"}`}>
                {active.stateLbl}
              </span>
            </div>
            <div className="timeline-tooltip-dur">⏱ {active.dur}</div>
            <div className="timeline-tooltip-range">
              <div>{active.fromTxt}</div>
              <div style={{ opacity: 0.6, textAlign: "center" }}>↓</div>
              <div>{active.toTxt}</div>
            </div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 16, fontSize: 11, marginTop: 12, color: "var(--hint)" }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "rgba(52, 199, 89, 0.65)", borderRadius: 2, marginRight: 4 }} />{t("charts.timeline_running")}</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "rgba(255, 59, 48, 0.65)", borderRadius: 2, marginRight: 4 }} />{t("charts.timeline_stopped")}</span>
      </div>
    </div>
  );
}

function StopReasonsChart({ events, t }: {
  events: TurbineEvent[];
  t: (k: string, p?: Record<string, string | number>) => string;
}) {
  const data = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of events) {
      if (e.event_type !== "stop") continue;
      const reason = (e.reason || "").trim() || t("charts.reason_unknown");
      counts.set(reason, (counts.get(reason) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [events, t]);
  const total = data.reduce((s, d) => s + d.value, 0);
  if (data.length === 0) {
    return <div className="empty-state">{t("charts.stop_reasons_empty")}</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
             outerRadius={100} label={(d) => `${Math.round((d.value as number) / total * 100)}%`}
             isAnimationActive={false}>
          {data.map((_, idx) => (
            <Cell key={idx} fill={TEMP_COLORS[idx % TEMP_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(v: number, name: string) => [`${v} (${Math.round(v / total * 100)}%)`, name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}


/* ============================================================================
 *  Shared helpers + tooltip
 * ========================================================================= */

function periodWindow(p: Period): { from: string; to: string } {
  const today = new Date();
  const ymd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const minus = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d;
  };
  switch (p) {
    case "today": return { from: ymd(today), to: ymd(today) };
    case "yest":  return { from: ymd(minus(1)), to: ymd(minus(1)) };
    case "7d":    return { from: ymd(minus(7)), to: ymd(today) };
    case "30d":   return { from: ymd(minus(30)), to: ymd(today) };
    case "all":   return { from: "2020-01-01", to: ymd(today) };
  }
}

function fmtAxisLabel(iso: string, lang: string): string {
  const d = new Date(iso);
  const locale = lang === "ru" ? "ru-RU" : lang === "fa" ? "fa-IR" : "en-GB";
  return d.toLocaleString(locale, {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtTooltipTime(iso: string, lang: string): string {
  const d = new Date(iso);
  const locale = lang === "ru" ? "ru-RU" : lang === "fa" ? "fa-IR" : "en-GB";
  return d.toLocaleString(locale, {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function fmtDurationShort(mins: number, t: (k: string) => string): string {
  if (mins < 60) return `${mins} ${t("dashboard.turbines.unit_min")}`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m === 0 ? `${h} ${t("dashboard.turbines.unit_h")}` : `${h} ${t("dashboard.turbines.unit_h")} ${m} ${t("dashboard.turbines.unit_min")}`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh === 0 ? `${d} ${t("dashboard.turbines.unit_d")}` : `${d} ${t("dashboard.turbines.unit_d")} ${rh} ${t("dashboard.turbines.unit_h")}`;
}

interface TooltipPayloadEntry {
  dataKey: string;
  name: string;
  value: number | null | undefined;
  color: string;
  payload: { tooltipTime?: string; label?: string };
}

function ChartAnalysis({ items, t }: {
  items: AnalysisItem[];
  t: (k: string, p?: Record<string, string | number>) => string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="chart-analysis">
      <div className="chart-analysis-title">{t("analysis.title")}</div>
      <ul className="chart-analysis-list">
        {items.map((it, idx) => (
          <li key={idx} className={`chart-analysis-row sev-${it.severity}`}>
            <span className="chart-analysis-dot" aria-hidden="true" />
            <span>{it.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChartTooltip({ active, payload }: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
}) {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload.filter(p => p.value !== null && p.value !== undefined);
  if (rows.length === 0) return null;
  const when = payload[0].payload.tooltipTime ?? payload[0].payload.label ?? "";
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-time">📅 {when}</div>
      {rows.map(p => (
        <div key={p.dataKey} className="chart-tooltip-row" style={{ color: p.color }}>
          <b>{p.name}:</b>{" "}
          <span style={{ fontFamily: "ui-monospace, monospace" }}>
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
}
